/**
 * LogEventListener
 * 
 * Listens for log events from ChronoSight and triggers alerts when log entries
 * match alert conditions. Handles events related to:
 * - Log levels (INFO, WARNING, CRITICAL)
 * - Log message content
 * 
 * @module LogEventListener
 */

import BaseEventListener from './BaseEventListener';
import { AlertType, LogEvents } from '../../models/AlertModels';

/**
 * Logger for LogEventListener - only essential logging
 */
const Logger = {
  log: () => {}, // No-op to silence informational logs
  warn: (...args) => console.warn('[LogEventListener]', ...args), // Keep warnings
  error: (...args) => console.error('[LogEventListener]', ...args), // Keep errors
  debug: () => {} // No-op to silence debug logs
};

/**
 * Listener for log events
 * @class LogEventListener
 * @extends BaseEventListener
 */
class LogEventListener extends BaseEventListener {
  /**
   * Creates an instance of LogEventListener
   * @param {Object} alertService - Service for alert operations
   */
  constructor(alertService) {
    super(alertService, 'Log');
    
    // Bind event handlers
    this._handleLogEvent = this._handleLogEvent.bind(this);
    
    // Track log entries for deduplication
    this.processedLogIds = new Set();
    this.maxProcessedLogIds = 1000; // Maximum number of log IDs to track
    

  }

  /**
   * Set up event listeners for log events
   * @protected
   * @returns {Promise<void>}
   */
  async _setupEventListeners() {
    // Add event listener for log events
    window.addEventListener('log-update', this._handleLogEvent);
    

  }

  /**
   * Remove event listeners for log events
   * @protected
   * @returns {Promise<void>}
   */
  async _removeEventListeners() {
    // Remove event listener for log events
    window.removeEventListener('log-update', this._handleLogEvent);
    

  }

  /**
   * Handle log event
   * @private
   * @param {Event} event - The event object
   */
  async _handleLogEvent(event) {
    if (!event.detail || !event.detail.serverId || !event.detail.data) {
      Logger.warn('Invalid log event received', event);
      return;
    }
    
    const { serverId, data, timestamp } = event.detail;
    
    // Safely handle timestamp conversion
    let eventTime;
    try {
      // First try to use the event_time from data if available
      if (data && data.event_time) {
        eventTime = data.event_time;
      } else if (timestamp && !isNaN(new Date(timestamp).getTime())) {
        // If timestamp is valid, convert it to ISO string
        eventTime = new Date(timestamp).toISOString();
      } else {
        // Fallback to current time if both are invalid
        eventTime = new Date().toISOString();
      }
    } catch (error) {
      Logger.warn('Error parsing timestamp, using current time', error);
      eventTime = new Date().toISOString();
    }
    
    // Extract log data from event
    const logData = this._extractLogData(data);
    if (!logData) {
      Logger.warn('Failed to extract log data from event', data);
      return;
    }
    
    // Generate a unique ID for this log entry to prevent duplicate processing
    const logId = this._generateLogId(serverId, logData);
    
    // Skip if we've already processed this log entry
    if (this.processedLogIds.has(logId)) {
      return;
    }
    
    // Add to processed logs and maintain maximum size
    this.processedLogIds.add(logId);
    if (this.processedLogIds.size > this.maxProcessedLogIds) {
      // Remove oldest entries when we exceed the maximum
      const entriesToRemove = this.processedLogIds.size - this.maxProcessedLogIds;
      const iterator = this.processedLogIds.values();
      for (let i = 0; i < entriesToRemove; i++) {
        const entry = iterator.next().value;
        this.processedLogIds.delete(entry);
      }
    }
    
    // Process the log entry to check for alerts
    await this._processLogEntry(serverId, logData, eventTime);
  }

  /**
   * Extract log data from various event formats
   * @private
   * @param {Object} eventData - The raw event data
   * @returns {Object|null} - The extracted log data or null if not found
   */
  _extractLogData(eventData) {
    if (!eventData) {
      Logger.warn('No event data to extract for log');
      return null;
    }
    
    Logger.debug('Extracting log data from event data:', JSON.stringify(eventData));
    
    // Handle different formats of Log data
    if (eventData.Log && typeof eventData.Log === 'object') {
      // Format 1: { Log: { level: "INFO", message: "..." } }
      return eventData.Log;
    } else if (eventData.event_data && typeof eventData.event_data === 'object') {
      // Format 2: { event_data: { level: "INFO", message: "..." } }
      return eventData.event_data;
    } else if (eventData.data && typeof eventData.data === 'object') {
      // Format 3: { data: { level: "INFO", message: "..." } }
      return eventData.data;
    } else {
      // Format 4: Direct format where the log data is at the root level
      const hasLogKeys = ['level', 'message'].some(key => key in eventData);
      
      if (hasLogKeys) {
        Logger.debug('Found log data in event data directly');
        return eventData;
      }
    }
    
    // Last resort: check for any string message or text properties
    if (typeof eventData.message === 'string' || typeof eventData.text === 'string' || typeof eventData.content === 'string') {
      Logger.debug('Found log message but missing other fields, creating partial data');
      return {
        message: eventData.message || eventData.text || eventData.content,
        level: eventData.level || eventData.severity || 'INFO',
        timestamp: eventData.timestamp || new Date().toISOString(),
        ...eventData // Include all original fields too
      };
    }
    
    Logger.warn('Failed to extract any log data from event data');
    return null;
  }

  /**
   * Generate a unique ID for a log entry
   * @private
   * @param {string} serverId - The ID of the server
   * @param {Object} logData - The log data
   * @returns {string} A unique ID for the log entry
   */
  _generateLogId(serverId, logData) {
    // Use a combination of server ID, timestamp, and message to create a unique ID
    const timestamp = logData.timestamp || '';
    const message = logData.message || '';
    const level = logData.level || '';
    
    return `${serverId}:${timestamp}:${level}:${message.substring(0, 50)}`;
  }

  /**
   * Process a log entry
   * @private
   * @param {string} serverId - The ID of the server
   * @param {Object} logData - The log data
   * @param {string} eventTime - The timestamp of the event
   */
  async _processLogEntry(serverId, logData, eventTime) {
    // Process each log property separately
    for (const property of LogEvents) {
      if (!(property in logData)) {
        continue; // Skip properties that don't exist in this log entry
      }
      
      const propertyValue = logData[property];
      
      // Create event data for this specific property
      const eventData = {
        event: property,
        value: propertyValue,
        serverId,
        timestamp: Date.now(),
        eventTime,
        log: logData // Include the full log data for context
      };
      
      // Process the event
      await this.handleEvent(eventData);
    }
  }

  /**
   * Handle an event by evaluating alerts and creating notifications
   * @param {Object} eventData - The event data
   * @returns {Promise<boolean>} True if the event was handled successfully
   */
  async handleEvent(eventData) {
    // Call the parent method to track statistics
    await super.handleEvent(eventData);
    
    if (!this.active) {

      return false;
    }
    
    try {

      
      // Validate that we have the required fields for alert evaluation
      if (!eventData.event) {
        Logger.warn('Event missing required field "event":', eventData);
        return false;
      }
      
      // Use the updated AlertService.evaluateEvent method which handles notification creation
      const { triggeredAlerts, notifications } = await this.alertService.evaluateEvent(
        eventData,
        AlertType.LOG,
        eventData.serverId
      );
      
      // Always log event processing for monitoring

      
      // Log triggered alerts
      if (triggeredAlerts.length > 0) {

        // No need to log each alert individually
      }
      
      // Log created notifications
      if (notifications.length > 0) {

        // No need to log each notification individually
      }
      
      return true;
    } catch (error) {
      Logger.error('Error handling log event', error);
      return false;
    }
  }
}

export default LogEventListener;
